import { rasterSrc } from "@/generated/raster-images";
import { createFileRoute } from "@tanstack/react-router";
import { PageHero, Section, SectionHeader, PillarTrio, CTABand } from "@/components/site-blocks";
import { CheckCircle2, Users, Calendar, Wrench, MapPin, ShieldCheck } from "lucide-react";
import installerAtWork from "@/assets/installer-at-work.webp.asset.json";

import { pageSeo } from "@/lib/seo";

export const Route = createFileRoute("/about")({
  head: () => ({
    ...pageSeo({
      path: "/about",
      title: "About CRS Energy | Warm, Efficient Homes Across Wales",
      description:
        "Family-founded 2018, CRS Energy is a PAS 2030 retrofit specialist with 10,000+ installations across Wales. Employee-owned since 2024.",
    }),
  }),
  component: AboutPage,
});

const STATS = [
  { icon: Calendar, label: "Established", value: "2018" },
  { icon: Wrench, label: "Installations completed", value: "10,000+" },
  { icon: MapPin, label: "Coverage", value: "Wales & surrounding areas" },
  { icon: Users, label: "In-house specialists", value: "Surveyors, Installers & Retrofit Team" },
  { icon: ShieldCheck, label: "Employee Ownership Trust", value: "Since 2024" },
];

function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About CRS Energy"
        title="Warmer homes. Lower bills. Built to last."
        description="Family-founded in 2018, we help households across Wales create warmer, safer, more energy-efficient homes, backed by a fully in-house team and Employee Ownership Trust stewardship since 2024."
        image={rasterSrc("stock/photo-1503387762-592deb58ef4e")}
        imageAlt="CRS Energy field team on site"
      />

      {/* Our Story */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <SectionHeader
              eyebrow="Our story"
              title="From one van to 10,000+ installations across Wales."
            />
            <div className="space-y-4 text-muted-foreground">
              <p>
                CRS Energy began in 2018 as a small, family-run business with a simple goal: to help
                households across Wales create warmer, safer, and more energy-efficient homes. With one
                van, a small team, and a commitment to quality workmanship, we quickly established a
                reputation for reliability, honesty, and a customer-first approach.
              </p>
            </div>
          </div>
          <img
            src={installerAtWork.url}
            alt="CRS Energy installer at work"
            className="rounded-2xl shadow-lift w-full h-[420px] object-cover"
            loading="lazy"
          />
        </div>

        {/* Stat strip */}
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STATS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                className="rounded-2xl border bg-card p-5 hover:shadow-soft transition-shadow"
              >
                <div className="rounded-xl bg-accent/10 text-accent p-2.5 w-fit">
                  <Icon className="h-5 w-5" aria-hidden />
                </div>
                <p className="mt-3 text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                  {s.label}
                </p>
                <p className="mt-1 text-base font-semibold leading-snug">{s.value}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Our Mission */}
      <Section className="bg-card">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <SectionHeader
            eyebrow="Our mission"
            title="Lower bills, greater comfort, and the funding you're entitled to."
          />
          <div className="space-y-4 text-muted-foreground">
            <p>
              Our mission is to support households across Wales in reducing energy bills, improving
              comfort, and accessing the government-backed funding they are entitled to. We are
              committed to delivering upgrades that are safe, compliant, and built to last, helping
              every customer enjoy a warmer home, lower running costs, and better living conditions.
            </p>
          </div>
        </div>
      </Section>

      {/* What We Stand For */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <SectionHeader
            eyebrow="What we stand for"
            title="Clear communication. Transparent advice. Installations that genuinely make a difference."
          />
          <div className="space-y-4 text-muted-foreground">
            <p>
              From the outset, our values have guided every aspect of our work. We believe in clear
              communication, transparent advice, and delivering installations that genuinely make a
              difference. Whether we are assessing a property, supporting a customer through
              eligibility checks, or completing a full upgrade, our priority is always to provide the
              right solution for the home.
            </p>
          </div>
        </div>
      </Section>

      {/* How We Work Today */}
      <Section className="bg-card">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <SectionHeader
            eyebrow="How we work today"
            title="One connected, in-house team, from enquiry to aftercare."
          />
          <div className="space-y-4 text-muted-foreground">
            <p>
              As demand for energy-efficiency improvements has grown, so have we. Today, our in-house
              team brings together skilled <strong>surveyors, installers, retrofit coordinators, and
              customer care specialists</strong>, all working within one connected process. Managing
              the journey under one roof means customers experience fewer delays, faster decisions,
              and a smoother path from enquiry to installation and aftercare.
            </p>
          </div>
        </div>
      </Section>

      {/* Employee Ownership Trust */}
      <Section>
        <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/85 text-primary-foreground p-8 md:p-12 shadow-lift">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-8 items-start">
            <div>
              <p className="text-xs font-semibold tracking-wider uppercase text-accent-soft mb-3">
                Since 2024
              </p>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight">
                Becoming an Employee Ownership Trust
              </h2>
            </div>
            <div className="space-y-4 text-primary-foreground/90">
              <p>
                In 2024, CRS Energy became an <strong>Employee Ownership Trust (EOT)</strong>,
                placing the company into the long-term stewardship of the people who work within it.
                This structure ensures stability, safeguards our values, and means every team member
                has a direct stake in the quality of the work we deliver.
              </p>
              <p>
                For customers, it provides added reassurance: the people improving your home are
                genuinely invested in getting it right.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Why Choose CRS Energy */}
      <Section className="bg-card">
        <SectionHeader
          eyebrow="Why choose CRS Energy"
          title="A trusted team committed to warm, efficient, cost-saving improvements."
          description="With CRS Energy, you're choosing care, professionalism, and installations completed to recognised industry standards."
        />
        <PillarTrio />
      </Section>

      {/* Industry Standards */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <SectionHeader
              eyebrow="Industry standards"
              title="Leading the standard in safe, compliant installations."
            />
            <p className="text-muted-foreground">
              CRS Energy operates to recognised industry and government standards, ensuring every
              installation is safe, compliant, and professionally delivered. Clear communication,
              qualified engineers you can trust, and dedicated support at every stage.
            </p>
          </div>
          <div className="rounded-2xl border bg-card p-7 shadow-soft">
            <div className="rounded-xl bg-accent/10 text-accent p-3 w-fit">
              <ShieldCheck className="h-7 w-7" aria-hidden />
            </div>
            <h3 className="mt-5 text-xl font-semibold">PAS-Certified Team</h3>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              We are accredited under <strong>PAS 2030</strong> and follow the <strong>PAS 2035</strong>{" "}
              retrofit framework. All heating work is completed by <strong>Gas Safe</strong> or{" "}
              <strong>MCS-certified</strong> engineers, giving you complete confidence in the quality
              of your upgrade.
            </p>
            <ul className="mt-5 space-y-2.5">
              {[
                "PAS 2030 accredited installer",
                "PAS 2035 retrofit framework",
                "Gas Safe registered heating engineers",
                "MCS-certified renewables team",
              ].map((item) => (
                <li key={item} className="flex gap-2.5 items-start text-sm">
                  <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <CTABand />
    </>
  );
}
